import {Avatar, Badge, Button} from 'platform/components';
import {Box, Clickable, HStack, Heading, Icon, Show, Text, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {KeyboardEvent, useId} from 'react';

import i18n from '@dms/i18n';

import {useBoolean} from 'shared/web';

import type {CustomerInsightsHistoryGroup as CustomerInsightsHistoryGroupModel} from '../../../types/CustomerInsightsViewModel';
import {CustomerInsightsDateMetadata} from './CustomerInsightsDateMetadata';
import {
  CustomerInsightsHistoryRecord,
  type CustomerInsightsHistoryAppearance,
} from './CustomerInsightsHistoryRecord';

interface CustomerInsightsHistoryGroupProps {
  group: CustomerInsightsHistoryGroupModel;
  kind: 'sales' | 'purchases' | 'lost' | 'service';
  title: string;
  /**
   * Expansion is owned by the parent so that a re-render which briefly replaces
   * this group (a retry, a refresh) cannot silently collapse it.
   */
  isExpanded: boolean;
  onToggleExpanded: VoidFunction;
}

const HISTORY_STYLES: Record<
  CustomerInsightsHistoryGroupProps['kind'],
  CustomerInsightsHistoryAppearance & {badgeColor: 'green' | 'orange' | 'red' | 'teal'}
> = {
  sales: {
    background: 'palettes.green.20.100',
    badgeColor: 'green',
    color: 'palettes.green.80.100',
    icon: 'editor/attach_money',
  },
  purchases: {
    background: 'palettes.orange.20.100',
    badgeColor: 'orange',
    color: 'palettes.orange.80.100',
    icon: 'action/swap_horiz',
  },
  lost: {
    background: 'palettes.red.20.100',
    badgeColor: 'red',
    color: 'palettes.red.80.100',
    icon: 'custom/business_case_sad',
  },
  service: {
    background: 'palettes.teal.20.100',
    badgeColor: 'teal',
    color: 'palettes.teal.80.100',
    icon: 'action/build',
  },
};

const MAX_LOAD_MORE_COUNT = 10;

const renderHistoryTotalBadge = (
  total: CustomerInsightsHistoryGroupModel['total'],
  style: (typeof HISTORY_STYLES)[CustomerInsightsHistoryGroupProps['kind']]
) =>
  match(total)
    .with(Pattern.number, (value) => (
      <Badge
        size="small"
        variant={value === 0 ? 'subtle' : 'bold'}
        colorScheme={value === 0 ? 'neutral' : style.badgeColor}
        value={value}
      />
    ))
    .otherwise(() => null);

export function CustomerInsightsHistoryGroup(props: CustomerInsightsHistoryGroupProps) {
  const [isHovered, handleMouseEnter, handleMouseLeave] = useBoolean();
  const contentId = useId();
  const hasRecords = props.group.items.length > 0;
  const isExpanded = props.isExpanded && hasRecords;
  const style = HISTORY_STYLES[props.kind];
  const iconBackground = hasRecords ? style.background : 'palettes.neutral.20.100';
  const iconColor = hasRecords ? style.color : 'palettes.neutral.300.100';
  const latestRecord = props.group.items[0];
  const visibleRecords = props.group.items;
  const loadMoreCount = match(props.group.total)
    .with(Pattern.number, (total) =>
      Math.min(MAX_LOAD_MORE_COUNT, total - props.group.items.length)
    )
    .otherwise(() => 0);
  const showLoadMore = props.group.hasMore && props.group.loadMore && loadMoreCount > 0;
  const showShowLess =
    props.group.isPaginationExpanded === true && props.group.showLess !== undefined;

  const toggleExpanded = () => {
    if (hasRecords) {
      props.onToggleExpanded();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleExpanded();
    }
  };

  return (
    <Box borderBottom="thin solid" borderColor="general.separator">
      <Clickable
        role="button"
        tabIndex={0}
        width="100%"
        aria-controls={hasRecords ? contentId : undefined}
        aria-expanded={hasRecords ? isExpanded : undefined}
        isDisabled={!hasRecords}
        onClick={toggleExpanded}
        onKeyDown={handleKeyDown}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Box
          width="100%"
          minHeight={12}
          paddingVertical={1}
          paddingHorizontal={4}
          backgroundColor={isHovered ? 'palettes.neutral.20.100' : 'general.white'}
        >
          <HStack width="100%" spacing={2} align="center">
            <Box width={7} flexShrink={0}>
              <Avatar
                size="small"
                variant="square"
                icon={style.icon}
                color={iconBackground}
                iconColor={iconColor}
                data-testid={`customerInsights-history-${props.kind}-${hasRecords ? 'icon' : 'emptyIcon'}`}
              />
            </Box>
            <HStack spacing={2} align="center" minWidth={44} maxWidth={48}>
              <Heading
                size={5}
                headingLevel={4}
                isSingleLine
                color={hasRecords ? 'primary' : 'secondary'}
              >
                {props.title}
              </Heading>
              {renderHistoryTotalBadge(props.group.total, style)}
            </HStack>
            <Show when={latestRecord}>
              <VStack spacing={1} minWidth={0} grow={1}>
                <Text
                  size="small"
                  alternative
                  noWrap
                  data-testid={`customerInsights-history-${props.kind}-latestTitle`}
                >
                  {latestRecord?.title}
                </Text>
                <Show when={latestRecord?.occurredAt}>
                  <CustomerInsightsDateMetadata
                    occurredAt={latestRecord?.occurredAt ?? ''}
                    data-testid={`customerInsights-history-${props.kind}-latest`}
                  />
                </Show>
              </VStack>
            </Show>
            <Show when={!hasRecords}>
              <Box flex={1} minWidth={0}>
                <Text size="small" color="secondary" noWrap inline>
                  <em>{i18n.t('entity.activityPanel.activity.noActivity')}</em>
                </Text>
              </Box>
            </Show>
            <Show when={hasRecords}>
              <Icon
                value={isExpanded ? 'navigation/arrow_up' : 'navigation/chevron_right'}
                size={4}
                color="palettes.neutral.500.100"
              />
            </Show>
          </HStack>
        </Box>
      </Clickable>
      <Show when={isExpanded}>
        <section data-testid={`customerInsights-history-${props.kind}-details`}>
          <Box
            id={contentId}
            role="list"
            backgroundColor="palettes.neutral.20.100"
            paddingVertical={1}
          >
            {visibleRecords.map((record, index) => (
              <CustomerInsightsHistoryRecord
                key={record.id}
                record={record}
                historyKind={props.kind}
                appearance={style}
                hasConnector={index < visibleRecords.length - 1}
              />
            ))}
          </Box>
        </section>
        <Show when={showLoadMore || showShowLess}>
          <Box paddingHorizontal={4} paddingVertical={1} backgroundColor="palettes.neutral.20.100">
            <HStack justify="center" spacing={4}>
              <Show when={showLoadMore}>
                <Button
                  size="small"
                  variant="link"
                  title={i18n.t('entity.customer.actions.moreWithCount', {count: loadMoreCount})}
                  isLoading={props.group.isLoadingMore}
                  data-testid={`customerInsights-history-${props.kind}-loadMore`}
                  onClick={props.group.loadMore}
                />
              </Show>
              <Show when={showShowLess}>
                <Button
                  size="small"
                  variant="link"
                  title={i18n.t('general.actions.showLess')}
                  data-testid={`customerInsights-history-${props.kind}-showLess`}
                  onClick={props.group.showLess}
                />
              </Show>
            </HStack>
          </Box>
        </Show>
      </Show>
    </Box>
  );
}
